<template>
  <div
    class="bb-table-wrapper mt-3 bb-popup-table-table-wrapper"
    id="board"
  >
    <div class="bb-popup-table-controls mb-3">
      <div
        v-if="searchEnabled"
        class="bb-viewer-search-shell"
      >
        <div class="form-group bb-viewer-search-wrap">
          <div class="form-floating">
            <v-select
              :options="[]"
              :clearable="false"
              :filterable="false"
              :dropdown-should-open="dropdownShouldOpen"
              class="form-control contents-dialog-select bb-viewer-search-select"
              placeholder="search..."
              id="bb-party-skills-search-input"
            >
              <template #search="{ attributes, events }">
                <input
                  v-bind="attributes"
                  :value="searchTerm"
                  @input="handleSearchInput"
                  @keydown="events.keydown"
                  @keypress="events.keypress"
                  @focus="events.focus"
                  @blur="events.blur"
                  @compositionstart="events.compositionstart"
                  @compositionend="events.compositionend"
                  class="vs__search bb-viewer-search-input"
                  style="
                    color: white !important;
                    -webkit-text-fill-color: white !important;
                    caret-color: white !important;
                    opacity: 1 !important;
                  "
                />
              </template>
            </v-select>
            <label
              class="bb-float-label"
              for="bb-party-skills-search-input"
              >Skill</label
            >
          </div>
        </div>
      </div>

      <div class="bb-popup-table-actions">
        <button
          type="button"
          class="btn btn-lg btn-primary"
          @click="$emit('close')"
        >
          Ok
        </button>
      </div>
    </div>

    <vue-good-table
      :columns="tableColumns"
      :rows="tableRows"
      :pagination-options="paginationOptions"
      :row-style-class="rowStyleClassFn"
      styleClass="vgt-table bordered"
      theme="black-rhino"
    >
      <template #table-column="props">
        <span v-if="props.column.field === 'skill_name'">
          {{ props.column.label }}
        </span>
        <div
          v-else
          class="bb-viewer-column-heading"
        >
          <span class="bb-viewer-column-name">
            {{ props.column.label }}
          </span>
          <span class="bb-viewer-column-subtitle">
            {{ props.column.playerLabel }}
          </span>
        </div>
      </template>

      <template #table-row="props">
        <span v-if="props.column.field === 'skill_name'">
          {{
            props.row.__placeholder
              ? ''
              : props.formattedRow[props.column.field]
          }}
        </span>
        <span v-else>
          {{ formatValue(props.formattedRow[props.column.field]) }}
        </span>
      </template>

      <template #emptystate></template>
    </vue-good-table>

    <div
      v-if="showStandaloneEmptyMessage"
      class="bb-empty-state"
    >
      {{ emptyMessage }}
    </div>
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'

import { VueGoodTable } from 'vue-good-table-next'

export default {
  name: 'PartySkillsTable',
  components: {
    VueGoodTable,
  },
  emits: ['update:searchTerm', 'close'],
  props: {
    partyCombatants: {
      type: Array,
      default: () => [],
    },
    npcCombatants: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    emptyMessage: {
      type: String,
      default: 'No skills to display.',
    },
    searchTerm: {
      type: String,
      default: '',
    },
    searchEnabled: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    allCombatants() {
      return [...this.partyCombatants, ...this.npcCombatants]
    },
    paginationOptions() {
      return {
        enabled: false,
        mode: 'records',
        perPage: 8,
        position: 'bottom',
        dropdownAllowAll: true,
        setCurrentPage: 1,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Skills per page',
        ofLabel: 'of',
        allLabel: 'All',
      }
    },
    tableColumns() {
      return [
        {
          label: 'Skill',
          field: 'skill_name',
          width: '7.5rem',
        },
        ...this.partyCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'No player',
          width: '6rem',
        })),
        ...this.npcCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'NPC',
          width: '6rem',
        })),
      ]
    },
    shouldShowPlaceholderRow() {
      return (
        this.allCombatants.length > 0 &&
        this.rows.length === 0 &&
        !this.searchTerm.trim()
      )
    },
    showStandaloneEmptyMessage() {
      return !this.shouldShowPlaceholderRow && !!this.emptyMessage
    },
    tableRows() {
      if (this.shouldShowPlaceholderRow) {
        const placeholderRow = {
          skill_name: ' ',
          __placeholder: true,
        }

        this.allCombatants.forEach((combatant) => {
          placeholderRow[this.getCombatantField(combatant)] = ''
        })

        return [placeholderRow]
      }

      return this.rows.map((row) => {
        const mappedRow = {
          skill_name: row.skill_name,
        }

        this.allCombatants.forEach((combatant) => {
          mappedRow[this.getCombatantField(combatant)] =
            row.values?.[combatant.character_id] ?? ''
        })

        return mappedRow
      })
    },
  },
  methods: {
    dropdownShouldOpen() {
      return false
    },
    handleSearchInput(event) {
      this.$emit('update:searchTerm', event.target.value)
    },
    handleSearch(value) {
      this.$emit('update:searchTerm', value)
    },
    getCombatantField(combatant) {
      return `character_${combatant.character_id}`
    },
    formatValue(value) {
      return value === null || value === undefined || value === '' ? '' : value
    },
    rowStyleClassFn(row) {
      if (row.originalIndex % 2 === 0) {
        return 'rowNeutral'
      }

      return 'rowParty'
    },
  },
}
</script>

<style scoped>
.bb-popup-table-table-wrapper {
  height: 64vh;
  max-height: 64vh;
  padding: 0.2rem 0.2rem 1rem;
}

/* .bb-viewer-search-shell {
  width: min(30rem, 100%);
  flex: 0 1 30rem;
} */

.bb-popup-table-controls {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

/* .bb-viewer-search-wrap {
  width: 100%;
  margin-bottom: 0;
}

.bb-viewer-search-select {
  width: 100%;
  max-width: 28rem;
  text-transform: none;
}

:deep(.bb-viewer-search-select .vs__dropdown-toggle) {
  border: none;
  min-height: 52px;
}

:deep(.bb-viewer-search-select .vs__actions) {
  display: none;
} */

/* :deep(.bb-viewer-search-input) {
  color: white !important;
  -webkit-text-fill-color: white !important;
  caret-color: white !important;
  opacity: 1 !important;
}

:deep(.bb-viewer-search-select .vs__selected-options) {
  color: white !important;
  -webkit-text-fill-color: white !important;
}

:deep(.bb-viewer-search-select .vs__search),
:deep(.bb-viewer-search-select .vs__selected),
:deep(.bb-viewer-search-select .vs__search::placeholder) {
  text-transform: none;
}

:deep(.bb-viewer-search-select .vs__search) {
  font-size: 1.4vw !important;
  font-weight: 350;
  padding-top: 0.4rem !important;
  border: none !important;
  line-height: 1.5 !important;
  color: white !important;
  -webkit-text-fill-color: white;
  caret-color: white;
}

:deep(.bb-viewer-search-select .vs__selected) {
  color: white !important;
  -webkit-text-fill-color: white;
}

:deep(.form-floating .bb-viewer-search-select:not(.vs--disabled) .vs__selected),
:deep(.form-floating .bb-viewer-search-select:not(.vs--disabled) .vs__search) {
  color: white !important;
  -webkit-text-fill-color: white !important;
}

:deep(.bb-viewer-search-select .vs__search::placeholder) {
  font-size: 1.5vw !important;
  font-weight: 350;
}

:deep(.bb-viewer-search-select .vs__search:placeholder-shown) {
  padding-top: 0.6rem !important;
}

:deep(.bb-viewer-search-select:has(.vs__search:focus)) {
  box-shadow: 0px 0px 2px 2px #25bfda !important;
} */

.bb-viewer-column-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  min-width: 0;
}

.bb-viewer-column-name {
  color: white;
  font-weight: inherit;
  font-size: inherit;
  line-height: 1.2;
}

.bb-viewer-column-subtitle {
  color: #737373;
  font-weight: 400;
  line-height: 1.2;
}

.bb-empty-state {
  padding: 0.5rem 0 0;
  color: white;
  font-size: 1.3rem;
}

.bb-popup-table-actions {
  display: flex;
  justify-content: flex-end;
  flex: 0 0 auto;
  padding-bottom: 0.2rem;
}

.bb-popup-table-actions .btn {
  width: auto;
  min-width: 8rem;
  margin-bottom: 0;
}

:deep(.vgt-table) {
  min-width: 50rem;
  table-layout: fixed;
}

:deep(.vgt-table thead th) {
  vertical-align: bottom;
  white-space: normal;
  padding-top: 10px;
  padding-bottom: 8px;
}

:deep(.vgt-table thead th:first-child),
:deep(.vgt-table tbody td:first-child) {
  width: 7.5rem;
  min-width: 7.5rem;
  max-width: 7.5rem;
}

:deep(.vgt-table thead th:not(:first-child)),
:deep(.vgt-table tbody td:not(:first-child)) {
  width: 6rem;
}

:deep(.vgt-table tbody td),
:deep(.vgt-table tbody th) {
  vertical-align: middle;
}

:deep(.vgt-table tbody td:first-child) {
  font-weight: 700;
  color: #c2ccd6;
}

:deep(.vgt-table tbody td:not(:first-child)) {
  text-align: center;
  font-weight: 600;
}

:deep(.vgt-wrap__footer) {
  border-top: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

@media (max-width: 768px) {
  .bb-popup-table-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .bb-viewer-search-shell {
    width: 100%;
  }

  .bb-popup-table-actions {
    justify-content: flex-start;
    padding-bottom: 0;
  }
}
</style>
